import { call, put, race, select, take } from 'typed-redux-saga';
import { CancelError } from 'lib/errors';
import { FilesContext } from 'types/types';
import { Platform } from 'react-native';
import { API_FILE_GENERIC, apiFilesPreview } from 'service/api/constants';
import { apiGet } from 'service/api';
import { EPFilesPreview } from 'types/Endpoints/Files';
import { getAssetThumbnailInline } from 'service/storage';
import { EPFileResponse } from 'types/Endpoints/Generic';
import { Image } from 'expo-image';
import delay from 'delay';
import { selectDownloadedThumbnail } from './sync/selectors';
import { selectBlockingLoaderCancelledKeys } from './app/selectors';
import { Confirm } from './app/types';
import {
  CONFIRM,
  eventSelectScreenClose,
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from './app/actions';
import {
  ModalScreens,
  NavigationStacks,
  Stacks,
} from 'components/Navigation/types';
import { randomKey } from 'lib/riskbase';

const fileIdToCached: Record<string, { filename: string }> = {};

export function* sagaWaitForCancel(waitKey: string) {
  do {
    const cancelledKeys = yield* select(selectBlockingLoaderCancelledKeys);
    if (cancelledKeys.includes(waitKey)) {
      throw new CancelError(waitKey);
    } else {
      yield delay(50);
    }
  } while (true);
}

export function* sagaConfirm(
  data: Omit<Confirm, 'onCancel' | 'onConfirm' | 'onAltConfirm'>,
) {
  const confirm = `SAGA_CONFIRM_CONFIRM_${Math.random()}`;
  const cancel = `SAGA_CONFIRM_CONFIRM_${Math.random()}`;
  const alt = `SAGA_CONFIRM_CONFIRM_ALT_${Math.random()}`;
  yield* put(
    CONFIRM({
      ...data,
      onConfirm: { type: confirm },
      onCancel: { type: cancel },
      onAltConfirm: data.altConfirmText ? { type: alt } : undefined,
    }),
  );
  const returnedType = (yield* take([confirm, cancel, alt])).type;
  if (returnedType === confirm) {
    return true;
  }
  if (returnedType === alt) {
    return 'alt';
  }
  return false;
}

export function* sagaSelect({
  selectConfig,
  onSelect,
  modalSize,
}: {
  selectConfig: Omit<
    NavigationStacks[Stacks.ROOT][ModalScreens.SELECT],
    | 'eventKeyAdded'
    | 'closeOnDone'
    | 'fieldName'
    | 'eventKey'
    | 'returnType'
    | 'incomingValueKey'
    | 'value'
  > & {
    value?: string[] | number[];
  };
  /**
   * Return false to keep select window open
   */
  // onSelect: (values: string[] | number[]) => Promise<boolean>;
  onSelect: (values: string[] | number[]) => Generator<unknown, boolean>;
  modalSize?: 'small' | 'fullscreen' | 'large';
}) {
  const eventKey = randomKey();

  yield* put(
    NAVIGATE_MODAL({
      screen: ModalScreens.SELECT,
      size: modalSize || 'small',
      params: {
        ...selectConfig,
        eventKey: eventKey,
        closeOnDone: false,
      },
    }),
  );

  let runAgain: boolean = true;
  let shouldClose: boolean = false;
  do {
    const yielded = yield* race({
      cancel: take(eventSelectScreenClose),
      select: take(eventSelectScreenSelection),
    });
    if (
      (yielded.cancel && yielded.cancel?.payload.key !== eventKey) ||
      (yielded.select && yielded.select?.payload.key !== eventKey)
    ) {
      // wrong eventKey - run 'do' again
    } else if (yielded.cancel) {
      runAgain = false;
    } else if (yielded.select) {
      shouldClose = true;
      runAgain = !(yield* call(
        onSelect,
        yielded.select?.payload.numberValues.length
          ? yielded.select?.payload.numberValues
          : yielded.select?.payload.stringValues,
      ));
    }
  } while (runAgain);

  if (shouldClose) {
    yield* put(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
  }
}

export function* getImagePreviewUri(
  fileId: string,
  context: FilesContext,
): Generator<
  unknown,
  {
    image: string;
  }
> {
  // Web
  if (Platform.OS === 'web') {
    try {
      if (!fileIdToCached[fileId]) {
        const response = yield* apiGet<EPFilesPreview['response']>(
          apiFilesPreview(fileId, context!),
        );
        const remoteFilename = API_FILE_GENERIC(response.filename);
        fileIdToCached[fileId] = {
          filename: remoteFilename,
        };
      }
      return { image: fileIdToCached[fileId]!.filename };
    } catch (_) {
      // Do nothing
    }
  } else {
    // Native
    try {
      let fileCache = fileIdToCached[fileId];
      const isCached = fileCache
        ? yield* call(Image.getCachePathAsync, fileCache.filename)
        : null;
      if (!isCached) {
        const response = yield* apiGet<EPFilesPreview['response']>(
          apiFilesPreview(fileId, context!),
        );
        const remoteFilename = API_FILE_GENERIC(response.filename);
        if (
          /** @ts-expect-error saga call not allowing Image.prefetch */
          yield* call(Image.prefetch, remoteFilename, { cachePolicy: 'disk' })
        ) {
          fileIdToCached[fileId] = {
            filename: remoteFilename,
          };
          fileCache = fileIdToCached[fileId];
        }
      }
      if (fileCache) {
        return { image: fileCache.filename };
      }
    } catch (_) {
      // Do nothing
    }
  }
  // Fallback to local thumbnail
  const downloadedThumbnail = yield* select(selectDownloadedThumbnail, fileId);
  if (!downloadedThumbnail) {
    throw new Error('Image is not available. Please connect to the internet');
  }
  const { assetId } =
    typeof downloadedThumbnail === 'string'
      ? { assetId: downloadedThumbnail }
      : downloadedThumbnail;
  const imageData = yield* call(getAssetThumbnailInline, assetId, fileId);
  return {
    image: imageData,
  };
}

export function* getFilePreviewUri(
  fileId: string,
  context: FilesContext,
  special?: {
    convert:
      | 'svgpdf'
      | 'pdfpage'
      | 'image'
      | 'svg'
      | 'pdf'
      | 'pdfimage'
      | undefined;
    page: number | null;
  },
) {
  return yield* apiGet<EPFileResponse>(
    apiFilesPreview(fileId, context!, special),
  );
}
